/* Meem Arabic — Gold Star Arabic Starter Pack.
   A standalone, section-scoped custom element; compatible with Shopify section reloads.
   Only performs cart writes if the section launch toggle is off and variant is available.
*/
(() => {
  if (customElements.get('meem-gold-starter')) return;

  class MeemGoldStarter extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.abortController = new AbortController();
      this._on = (el, event, handler) => {
        if (el) el.addEventListener(event, handler, { signal: this.abortController.signal });
      };
      this.q = selector => this.querySelector(selector);
      this.qa = selector => Array.from(this.querySelectorAll(selector));
      this.comingSoon = this.dataset.comingSoon === 'true';
      this.australiaOnly = this.dataset.australiaOnly === 'true';
      this.inAustralia = this.dataset.inAustralia === 'true';
      try {
        this.variants = JSON.parse(this.q('[data-variants-json]').textContent).variants;
      } catch (err) {
        this.variants = {};
      }
      this.variantInput = this.q('[data-variant]');
      this.quantityInput = this.q('[data-quantity]');
      this.addButton = this.q('[data-add-button]');
      this.form = this.q('.msp-product-form');
      this.status = this.q('[data-form-status]');
      this.checkCart = this.q('[data-check-cart]');
      this.busy = false;
      this.pendingUnknown = false;

      this.thumbnails = this.qa('.msp-thumbnail');
      this.galleryIndex = 0;
      this.dialog = this.q('[data-lightbox]');
      this.photo = this.q('[data-current-photo]');
      this.zoomTrigger = null;

      this._on(this.form, 'submit', event => {
        event.preventDefault();
        this.addToCart();
      });
      this._on(this.variantInput, 'change', () => {
        this.updatePrice();
        const url = new URL(location.href);
        url.searchParams.set('variant', this.variantInput.value);
        try { window.history.replaceState({}, '', url); } catch (_) {}
      });
      this._on(this.q('[data-decrease]'), 'click', () => this.setQuantity(this.currentQuantity() - 1));
      this._on(this.q('[data-increase]'), 'click', () => this.setQuantity(this.currentQuantity() + 1));
      this._on(this.quantityInput, 'blur', () => this.setQuantity(this.currentQuantity()));
      this._on(this.quantityInput, 'change', () => this.setQuantity(this.currentQuantity()));

      this.thumbnails.forEach((thumb, i) => {
        this._on(thumb, 'click', () => this.changeImage(i));
      });
      this._on(this.q('[data-gallery-prev]'), 'click', () => this.changeImage(this.galleryIndex - 1));
      this._on(this.q('[data-gallery-next]'), 'click', () => this.changeImage(this.galleryIndex + 1));
      this._on(this.q('[data-zoom-trigger]'), 'click', () => {
        const thumb = this.thumbnails[this.galleryIndex];
        if (thumb) this.openLightbox(thumb.dataset.full, thumb.dataset.alt, this.q('[data-zoom-trigger]'));
      });
      this.qa('[data-peek-zoom]').forEach(button => {
        this._on(button, 'click', () => this.openLightbox(button.dataset.zoomUrl, button.dataset.zoomAlt, button));
      });
      this._on(this.q('[data-lightbox-close]'), 'click', () => this.dialog && this.dialog.close());
      this._on(this.dialog, 'click', event => {
        if (event.target === this.dialog && this.dialog.open) this.dialog.close();
      });
      this._on(this.dialog, 'close', () => {
        if (this.zoomTrigger) this.zoomTrigger.focus({ preventScroll: true });
      });
      this._on(this, 'keydown', event => {
        if (this.dialog && this.dialog.open) return;
        if (event.target.closest('[data-gallery]') && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
          event.preventDefault();
          this.changeImage(this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      const mainPhoto = this.q('[data-zoom-trigger]');
      if (mainPhoto) {
        let touchStartX = 0;
        this._on(mainPhoto, 'touchstart', e => { touchStartX = e.changedTouches[0]?.screenX || 0; });
        this._on(mainPhoto, 'touchend', e => {
          const touchEndX = e.changedTouches[0]?.screenX || 0;
          if (Math.abs(touchStartX - touchEndX) >= 60 && this.thumbnails.length > 1) {
            this.changeImage(this.galleryIndex + (touchStartX > touchEndX ? 1 : -1));
          }
        });
      }
      this.updateGallery();
      this.updatePrice();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this._connected = false;
    }

    currentVariant() {
      return this.variants[String(this.variantInput?.value)] || null;
    }
    maxQuantity() {
      return Number(this.quantityInput?.max) || 10;
    }
    currentQuantity() {
      const parsed = Math.floor(Number(this.quantityInput?.value));
      return Number.isFinite(parsed) ? parsed : 1;
    }
    setQuantity(value) {
      if (!this.quantityInput) return;
      this.quantityInput.value = String(Math.max(1, Math.min(this.maxQuantity(), value)));
      this.q('[data-decrease]').disabled = Number(this.quantityInput.value) <= 1;
      this.q('[data-increase]').disabled = Number(this.quantityInput.value) >= this.maxQuantity();
    }

    updatePrice() {
      const v = this.currentVariant();
      if (!v) return;
      this.qa('[data-price], [data-mobile-price], [data-value-price]').forEach(el => { el.textContent = v.price; });
      const compare = this.q('[data-compare]');
      const percent = this.q('[data-percent]');
      const saving = this.q('[data-saving]');
      compare.hidden = !v.compare;
      percent.hidden = !v.percent;
      saving.hidden = !v.saving;
      compare.textContent = v.compare || '';
      percent.textContent = v.percent ? `${v.percent}% OFF` : '';
      saving.textContent = v.saving ? `You save ${v.saving} off the regular price` : '';
      let ctaText = 'Add Starter Pack to cart ↗';
      if (this.comingSoon) ctaText = 'Coming soon — Add to cart at launch';
      else if (this.australiaOnly && !this.inAustralia) ctaText = 'Currently available in Australia only';
      else if (!v.available) ctaText = 'Currently unavailable';
      if (this.pendingUnknown) ctaText = 'Please check your cart';
      if (this.busy) ctaText = 'Adding your pack…';
      this.addButton.textContent = ctaText;
      this.addButton.disabled = this.comingSoon || (this.australiaOnly && !this.inAustralia) || !v.available || this.pendingUnknown || this.busy;
      if (this.variantInput) this.variantInput.disabled = this.busy;
      this.setQuantity(this.currentQuantity());
      this.q('[data-decrease]').disabled = this.busy || this.currentQuantity() <= 1;
      this.q('[data-increase]').disabled = this.busy || this.currentQuantity() >= this.maxQuantity();
    }

    updateGallery() {
      const haveMore = this.thumbnails.length > 1;
      this.q('[data-gallery-prev]').disabled = !haveMore;
      this.q('[data-gallery-next]').disabled = !haveMore;
      const counter = this.q('[data-gallery-count]');
      if (counter) counter.textContent = this.thumbnails.length ? `Photo ${this.galleryIndex + 1} of ${this.thumbnails.length}` : 'More photos coming soon';
    }
    changeImage(index) {
      if (!this.thumbnails.length || !this.photo) return;
      this.galleryIndex = (index + this.thumbnails.length) % this.thumbnails.length;
      const chosen = this.thumbnails[this.galleryIndex];
      this.photo.removeAttribute('srcset');
      this.photo.removeAttribute('sizes');
      this.photo.src = chosen.dataset.display;
      this.photo.alt = chosen.dataset.alt || 'Gold Star Starter Pack photo';
      if (chosen.dataset.width) this.photo.width = Number(chosen.dataset.width);
      if (chosen.dataset.height) this.photo.height = Number(chosen.dataset.height);
      this.thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.galleryIndex);
        thumb.setAttribute('aria-pressed', i === this.galleryIndex ? 'true' : 'false');
      });
      chosen.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this.updateGallery();
    }
    openLightbox(url, alt, trigger) {
      if (!url || !this.dialog) return;
      if (typeof this.dialog.showModal !== 'function') {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      this.zoomTrigger = trigger;
      const image = this.q('[data-lightbox-image]');
      image.src = url;
      image.alt = alt || 'Starter Pack product detail';
      if (!this.dialog.open) this.dialog.showModal();
    }

    async addToCart() {
      const v = this.currentVariant();
      if (!v || !v.available || this.comingSoon || (this.australiaOnly && !this.inAustralia) || this.busy || this.pendingUnknown) return;
      this.status.textContent = '';
      this.checkCart.hidden = true;
      this.busy = true;
      this.updatePrice();
      this.status.textContent = 'Adding your Starter Pack…';
      const timeout = new AbortController();
      const timer = setTimeout(() => timeout.abort(), 20000);
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(this.variantInput.value), quantity: this.currentQuantity() }] }),
          signal: timeout.signal
        });
        let result;
        try { result = await response.json(); }
        catch (_) { throw new Error('Could not confirm cart response'); }
        if (!response.ok) {
          if (response.status === 422 && result?.description) {
            this.status.textContent = result.description;
            return;
          }
          this.pendingUnknown = true;
          this.status.textContent = result?.description || 'We could not confirm whether your pack was added.';
          this.checkCart.hidden = false;
          return;
        }
        this.pendingUnknown = true;
        this.status.textContent = 'Added — opening your cart…';
        window.location.assign(this.dataset.cartUrl);
      } catch (_) {
        this.pendingUnknown = true;
        this.status.textContent = 'We could not confirm whether your pack was added. Please check your cart before trying again.';
        this.checkCart.hidden = false;
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.updatePrice();
      }
    }
  }
  customElements.define('meem-gold-starter', MeemGoldStarter);
})();
